import { today } from '@seen/core/dates'
import { CoverageType, ExtendedCoverage } from '@seen/core/models/coverage'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { useForm } from '#src/hooks/useForm.ts'

const coverageFormSchema = z
  .object({
    type: z.nativeEnum(CoverageType),
    startDate: z.string({ required_error: 'Start date is required' }).date(),
    endDate: z.string().date().nullable(),
    subscriberId: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.endDate && new Date(data.endDate) < new Date(data.startDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date must be after start date',
        path: ['endDate'],
      })
    }

    if (data.subscriberId && data.type !== CoverageType.SEEN_HEALTH) {
      const result = ExtendedCoverage.validateInsuranceId(
        data.subscriberId,
        data.type
      )
      if (!result.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result.error ?? 'Invalid ID format',
          path: ['subscriberId'],
        })
      }
    }
  })

export type CoverageFormValues = z.infer<typeof coverageFormSchema>

interface CoverageFormProps {
  defaultValues?: Partial<CoverageFormValues>
  onSubmit: (values: CoverageFormValues) => void
  submitLabel: string
  isSubmitting?: boolean
  isEditing?: boolean
  onDelete?: () => void
  isDeleting?: boolean
  disabled?: boolean
}

export function CoverageForm({
  defaultValues,
  onSubmit,
  submitLabel,
  isSubmitting = false,
  isEditing = false,
  onDelete,
  isDeleting = false,
  disabled = false,
}: CoverageFormProps): JSX.Element {
  const form = useForm({
    schema: coverageFormSchema,
    defaultValues: {
      type: CoverageType.MEDICARE,
      startDate: today(),
      endDate: null,
      subscriberId: null,
      ...defaultValues,
    },
  })

  const coverageType = form.watch('type') as CoverageType
  const subscriberIdField =
    ExtendedCoverage.getSubscriberIdFieldInfo(coverageType)

  const handleSubmit = form.handleSubmit((values) => {
    onSubmit(values as CoverageFormValues)
  })

  return (
    <Form onSubmit={handleSubmit}>
      <div className="space-y-8">
        <div className="flex flex-1 gap-4">
          <div className="flex flex-col">
            <FormLabel className="text-sm font-medium">Coverage Type</FormLabel>
            {isEditing ? (
              <div className="flex h-9 items-center text-sm">
                {
                  ExtendedCoverage.TYPE_OPTIONS.find(
                    (o) => o.value === coverageType
                  )?.label
                }
              </div>
            ) : (
              <FormField
                control={form.control}
                name="type"
                render={({ field: { value, onChange, ...fieldProps } }) => (
                  <ComboboxButton
                    options={ExtendedCoverage.TYPE_OPTIONS}
                    value={value as CoverageType}
                    onChange={(v) => onChange(v as CoverageType)}
                    {...fieldProps}
                  />
                )}
              />
            )}
          </div>
          <div className="flex flex-col">
            <FormLabel className="text-sm font-medium">Start Date</FormLabel>
            <FormField
              control={form.control}
              name="startDate"
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <DatePicker
                  {...fieldProps}
                  value={value as string}
                  onChange={(v) => onChange(v ?? '')}
                />
              )}
            />
          </div>
          <div className="flex flex-col">
            <FormLabel className="text-sm font-medium">End Date</FormLabel>
            <FormField
              control={form.control}
              name="endDate"
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <DatePicker
                  {...fieldProps}
                  value={value as string | null}
                  onChange={onChange}
                  allowUndefined
                />
              )}
            />
          </div>
        </div>
        {subscriberIdField && (
          <div className="flex flex-col">
            <FormLabel className="text-sm font-medium">
              {subscriberIdField.label}
            </FormLabel>
            <FormField
              control={form.control}
              name="subscriberId"
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <Input
                  {...fieldProps}
                  value={(value as string | null) ?? ''}
                  onChange={(e) => onChange(e.target.value || null)}
                  placeholder={subscriberIdField.placeholder}
                />
              )}
            />
          </div>
        )}
        <div className="flex items-center justify-between">
          <Button type="submit" loading={isSubmitting} disabled={disabled}>
            {submitLabel}
          </Button>
          {onDelete && (
            <Button
              type="button"
              variant="destructive"
              loading={isDeleting}
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
    </Form>
  )
}
